export interface AppointmentType {
  ehrId: number;
  name: string;
  abbreviation: string;
  durationCategory: 'short' | 'medium' | 'long' | 'other' | 'unknown' | '';
  doesTrapezeSchedule: boolean;
  canScheduleForDoshi: boolean;
  canScheduleForKligman: boolean;
  canScheduleForBerliner: boolean;
}

export interface SchedulingRule {
  category: 'appointment-determination' | 'slot-matching' | 'limitations';
  title: string;
  rules: string[];
}

export const mentalModelRules: SchedulingRule[] = [
  {
    category: 'appointment-determination',
    title: 'Appointment Type Determination Rules',
    rules: [
      'Iris takes into account: Patient conversation, A&P (Assessment & Plan), and set of possible appointment types',
      'Iris can only book appointment types that Trapeze can handle',
      'If Iris cannot ever schedule an appointment type, Iris will offer to transfer to the PCC or the office',
      'If patient says an appointment type and does not have A&P, respect their appointment type',
      "If patient has a general/unspecific request that doesn't follow an appointment type, follow A&P",
      'If the patient has a specific request that conflicts with A&P, respect the appointment type that the patient requested',
      "If the patient has no A&P and doesn't clarify need/request, book existing patient as follow up",
      'If the patient is a new patient and has a specific request, book with one of consult types',
      'If the patient is a new patient and does not have a specific request, book as a general NPT',
      'If patient indicates specific urgency or symptoms outlined in common emergency symptoms, select Emergency',
    ],
  },
  {
    category: 'slot-matching',
    title: 'Appointment Slotting Rules',
    rules: [
      'Iris will read iMedicWare provider schedule, including each scheduling slot and template labels',
      'If the labels have specific requirements, Iris respects them',
      'Iris can always book appointments in slots that match the template labels',
      'If request is for "earliest" or date within 7 days and there are empty slots, book in any available slot',
      'If not within 7 days: Long appointments cannot be scheduled back to back',
      'If not within 7 days: No long appointments can be booked 30 mins before lunch, or 1 hr before end of day',
      "If not within 7 days: Testing appointments cannot be scheduled in the last 30 minutes of the provider's day",
      'Iris never double books',
    ],
  },
  {
    category: 'limitations',
    title: 'Other Limitations',
    rules: [
      'Iris cannot book families or multiple patients in 1 call today',
      'Emergency appointments are not handled by Iris',
      'Iris will transfer to appropriate personnel when unable to handle request',
    ],
  },
];

export const appointmentTypes: AppointmentType[] = [
  {
    ehrId: 3580,
    name: '67036',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3581,
    name: '67041',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3582,
    name: '67042',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3583,
    name: '67113',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3538,
    name: '1 Day Post Op',
    abbreviation: '1DPO',
    durationCategory: 'short',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3542,
    name: '1 Month Post Op',
    abbreviation: '1MPO',
    durationCategory: 'short',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3541,
    name: '1 Week Post Op',
    abbreviation: '1WPO',
    durationCategory: 'short',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3540,
    name: '3 Day Post Op',
    abbreviation: '3DPO',
    durationCategory: 'short',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 203,
    name: '3 Month Follow Up',
    abbreviation: '3MO',
    durationCategory: 'medium',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 77,
    name: '4 Month Follow Up',
    abbreviation: '4MO',
    durationCategory: 'medium',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 73,
    name: '6 Month Follow Up',
    abbreviation: '6 MO',
    durationCategory: 'medium',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3604,
    name: '67145-Retinopexy',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 291,
    name: 'Adult Hearing Eval',
    abbreviation: 'AHE',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3586,
    name: 'Ahmed Tube ASC',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 82,
    name: 'Annual Eye Exam',
    abbreviation: 'Yearly',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 320,
    name: 'Annual Hearing Evaluation',
    abbreviation: 'AUE',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3445,
    name: 'Ant. Orbitotomy',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3623,
    name: 'Ascan',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 504,
    name: 'Aud Follow Up',
    abbreviation: 'AUD FUP',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3524,
    name: 'Baerveldt Tube Shunt',
    abbreviation: 'Baerveldt Tube Shunt',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 458,
    name: 'Bilateral Lateral Rectus Recession',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 459,
    name: 'Bilateral Medial Rectus Recession',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 350,
    name: 'Bleph',
    abbreviation: 'Bleph',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 375,
    name: 'Bleph Eval',
    abbreviation: 'BEVAL',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 378,
    name: 'Botox Injection',
    abbreviation: 'BOT',
    durationCategory: 'unknown',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3693,
    name: 'Botox Promo',
    abbreviation: '',
    durationCategory: 'unknown',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 367,
    name: 'Browplasty',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 50,
    name: 'Cataract 1st Eye Pre-Op',
    abbreviation: 'CPRE1',
    durationCategory: 'long',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 451,
    name: 'Cataract 2nd Eye Pre-Op',
    abbreviation: 'CPRE2',
    durationCategory: 'medium',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 8,
    name: 'Cataract Evals/Consults',
    abbreviation: 'CAE',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 363,
    name: 'Chalazion Incision & Drainage',
    abbreviation: 'CID',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 393,
    name: 'Chelation',
    abbreviation: 'Chelation',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3545,
    name: 'Child New Patient',
    abbreviation: '',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3546,
    name: 'Child Yearly',
    abbreviation: '',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3547,
    name: 'Clinical Research',
    abbreviation: 'Clinical Research',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3691,
    name: 'Clinical Research - Retina',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 552,
    name: 'Conjunctival Scar Removal',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3548,
    name: 'Conjunctivoplasty',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 421,
    name: 'Consult',
    abbreviation: 'Consult',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3621,
    name: 'Contact Lens Fit - Established',
    abbreviation: 'CTL Estab Fit',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3619,
    name: 'Contact Lens Fit - Initial',
    abbreviation: 'CTL Initial Fit',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3089,
    name: 'Contact Lens follow up',
    abbreviation: 'CTL FUP',
    durationCategory: 'short',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 316,
    name: 'Cornea Follow Up',
    abbreviation: 'CFU',
    durationCategory: 'short',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 314,
    name: 'Cornea New Patient/Consults',
    abbreviation: 'CNP',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3612,
    name: 'Corneal Eval',
    abbreviation: '',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 435,
    name: 'Corneal Transplant',
    abbreviation: 'COPLNT',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 486,
    name: 'Cosmetic Consultation',
    abbreviation: 'CCO',
    durationCategory: 'unknown',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3473,
    name: 'Cosmetic Event',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3613,
    name: 'Cosmetic Surgery Eval',
    abbreviation: 'COSEVAL',
    durationCategory: 'other',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 547,
    name: 'Cross Linking',
    abbreviation: 'CRL',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3585,
    name: 'Cyclodiode',
    abbreviation: 'Cyclodiode',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 354,
    name: 'DCR',
    abbreviation: 'DCR',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3618,
    name: 'Demo Procedure',
    abbreviation: 'Demo Procedure',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3529,
    name: 'Dextenza',
    abbreviation: 'DTX',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3631,
    name: 'Diabetic Retinopathy',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 24,
    name: 'Dilated LASIK Exam',
    abbreviation: 'DLE',
    durationCategory: 'medium',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 377,
    name: 'Dilated PRK Exam',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 522,
    name: 'Dilated PTK Exam',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3632,
    name: 'Dislocated Lens',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 551,
    name: 'DMEK',
    abbreviation: 'DMEK',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3549,
    name: 'DSAEK',
    abbreviation: 'DSAEK',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3530,
    name: 'Durysta',
    abbreviation: 'DUR',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3690,
    name: 'eCO2 Laser',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3550,
    name: 'Ectropian Repair',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 175,
    name: 'Emergency',
    abbreviation: 'EMER',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 281,
    name: 'Enhancement',
    abbreviation: 'ENH',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 399,
    name: 'Enhancement Eval',
    abbreviation: 'Enh-eval',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3551,
    name: 'Entropion',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3552,
    name: 'Excision',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 302,
    name: 'FA',
    abbreviation: 'FA',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 396,
    name: 'Femto',
    abbreviation: 'FEM',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 272,
    name: 'Follow Up',
    abbreviation: 'FUP',
    durationCategory: 'short',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 557,
    name: 'Glaucoma Evaluation/Consults',
    abbreviation: 'Glaucoma Eval',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3646,
    name: 'Glaucoma Surgery',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3647,
    name: 'Glaucoma Testing',
    abbreviation: 'GTU',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3645,
    name: 'Goniotomy',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3579,
    name: 'Hearing Aid Check',
    abbreviation: 'HAC',
    durationCategory: 'unknown',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 318,
    name: 'Hearing Aid Consult (N/C)',
    abbreviation: 'HACon',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 295,
    name: 'Hearing Aid Dispense',
    abbreviation: 'HAD',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3634,
    name: 'Hearing Promo Screening',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3633,
    name: 'ICL Evaluation',
    abbreviation: 'ICL Eval',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3587,
    name: 'ICL Surgery',
    abbreviation: 'ICL',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3536,
    name: 'iLUX',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3553,
    name: 'IME',
    abbreviation: 'IME',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 271,
    name: 'IMPORT',
    abbreviation: 'IMPORT',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 461,
    name: 'Inferior Oblique Anterior Transposition',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 460,
    name: 'Inferior Oblique Myectomy',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 537,
    name: 'Inferior Oblique Recession',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 592,
    name: 'Injection',
    abbreviation: 'INJ',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3554,
    name: 'Intralase',
    abbreviation: 'INT',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3555,
    name: 'IOL Exchange',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3626,
    name: 'IPL',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3404,
    name: 'Istent',
    abbreviation: 'Istent',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3556,
    name: 'Juvederm',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3460,
    name: 'Kahook',
    abbreviation: 'Kahook',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 544,
    name: 'Kybella',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3364,
    name: 'LASER - 67105',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3557,
    name: 'LASER - FOCAL',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3559,
    name: 'LASER-PRP',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 5,
    name: 'Lasik Evals/Consult',
    abbreviation: 'LAE',
    durationCategory: 'long',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 466,
    name: 'Lateral Rectus Resection',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 471,
    name: 'Lateral Tarsal Strip',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 382,
    name: 'Lens Repositioning',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3523,
    name: 'LipiFlow',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3478,
    name: 'Long',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3558,
    name: 'LPI Laser',
    abbreviation: 'LPI',
    durationCategory: 'short',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 286,
    name: 'LRI',
    abbreviation: 'LRI',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3629,
    name: 'Macular Hole',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 467,
    name: 'Medial Rectus Recession on adjustable',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 472,
    name: 'Medial Spindle',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3584,
    name: 'Micropulse Laser',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3544,
    name: 'Minor Procedure',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3562,
    name: 'Mohs Repair',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 337,
    name: 'Neuro Oph Exam',
    abbreviation: 'NOE',
    durationCategory: 'long',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 4,
    name: 'New Patient',
    abbreviation: 'NPT',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 310,
    name: 'NO RECALL',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 591,
    name: 'ORA',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3197,
    name: 'Pars Plana Vitrectomy',
    abbreviation: 'PPV',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 293,
    name: 'Pediatric Hearing Eval',
    abbreviation: 'PHE',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3682,
    name: 'Phaco IOL with ECP',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 125,
    name: 'Phaco w/IOL',
    abbreviation: 'CAT',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 223,
    name: 'Plaquenil Screening',
    abbreviation: 'PLAQ',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 447,
    name: 'Plastic Repair of canaliculi',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 593,
    name: 'Possible Injection',
    abbreviation: 'PIN',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 204,
    name: 'Post OP',
    abbreviation: 'PO',
    durationCategory: 'short',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3563,
    name: 'Posterior Anterior Vitrectomy',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3564,
    name: 'PRE OP',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3684,
    name: 'Pre-Surgical Clearance',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3590,
    name: 'Prescription Check',
    abbreviation: 'Rx Check',
    durationCategory: 'short',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3624,
    name: 'Prism Evaluation',
    abbreviation: '',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3565,
    name: 'PRK',
    abbreviation: 'PRK',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 488,
    name: 'Probe and Irrigation',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3566,
    name: 'Prokera / Biovance',
    abbreviation: 'Pro/Bio',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3635,
    name: 'Promptly - Annual Eye Exam',
    abbreviation: 'Promptly - Annual',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3694,
    name: 'Promptly - Botox Promo',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3640,
    name: 'Promptly - Cataract Eval',
    abbreviation: 'Promptly - CAE',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3638,
    name: 'Promptly - Contact Lens Fit',
    abbreviation: 'Promptly - CTL Fit',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3642,
    name: 'Promptly - Cornea Eval',
    abbreviation: 'Promptly - Cornea Eval',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3643,
    name: 'Promptly - Cosmetic Eval',
    abbreviation: 'Promptly - Cosmetic Eval',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3637,
    name: 'Promptly - Follow Up',
    abbreviation: 'Promptly - FUP',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3641,
    name: 'Promptly - Glaucoma Eval',
    abbreviation: 'Promptly - Glaucoma Eval',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3639,
    name: 'Promptly - Lasik Eval',
    abbreviation: 'Promptly - LAE',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3636,
    name: 'Promptly - New Patient',
    abbreviation: 'Promptly - NPT',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3671,
    name: 'Promptly - Retina Evaluation',
    abbreviation: 'Promptly - Retina',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3567,
    name: 'Prophylaxis Laser',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3206,
    name: 'Pterygium Excision',
    abbreviation: 'Pterygium Excision',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 335,
    name: 'PTK',
    abbreviation: 'PTK',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3577,
    name: 'Ptosis Repair',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 468,
    name: 'Punctoplasty Bilateral',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3568,
    name: 'Pupil Plasty',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 477,
    name: 'Radiesse',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 513,
    name: 'Recess Resect Rectus Muscles',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 311,
    name: 'Removal of lens fragment',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3588,
    name: 'Reposition of Intraocular Lens',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3569,
    name: 'RESTYLANE DEFYNE',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 542,
    name: 'Restylane Silk',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3570,
    name: 'Retina Evaluation',
    abbreviation: 'RET Eval',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 3630,
    name: 'Retinal Detachment',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3589,
    name: 'Routine Exam',
    abbreviation: 'Routine',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 536,
    name: 'Ruptured Globe Repair',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3071,
    name: 'Short',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3560,
    name: 'SLT Laser',
    abbreviation: 'SLT',
    durationCategory: 'short',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3572,
    name: 'Strabismus',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 549,
    name: 'Superficial Keratectomy',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3573,
    name: 'Suture Removal',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 366,
    name: 'Tarsorrhaphy',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3652,
    name: 'TearCare',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3515,
    name: 'Telemedicine',
    abbreviation: 'TME',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 419,
    name: 'Testing Only',
    abbreviation: 'Testing Only',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3574,
    name: 'Trabeculectomy',
    abbreviation: 'TRAB',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3575,
    name: 'Trichiasis Correction',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3543,
    name: 'Tube Shunt',
    abbreviation: 'TS',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 40,
    name: 'Uveitis Followup',
    abbreviation: 'UV FUP',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 307,
    name: 'Uveitis Initial Exam',
    abbreviation: 'UVI',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3476,
    name: 'Verisyse',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3644,
    name: 'Viscocanalostomy',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 338,
    name: 'Visual Field',
    abbreviation: 'VF',
    durationCategory: 'long',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: true,
    canScheduleForKligman: true,
    canScheduleForBerliner: true,
  },
  {
    ehrId: 539,
    name: 'Volbella',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3576,
    name: 'XEOMIN',
    abbreviation: '',
    durationCategory: '',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3561,
    name: 'YAG Laser',
    abbreviation: 'YAG',
    durationCategory: 'short',
    doesTrapezeSchedule: true,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
  {
    ehrId: 3510,
    name: 'Zocdoc',
    abbreviation: 'Zocdoc',
    durationCategory: 'other',
    doesTrapezeSchedule: false,
    canScheduleForDoshi: false,
    canScheduleForKligman: false,
    canScheduleForBerliner: false,
  },
];

// Helper functions for filtering and analysis
export function getSchedulableAppointments(): AppointmentType[] {
  return appointmentTypes.filter((apt) => apt.doesTrapezeSchedule);
}

export function getProviderSpecificAppointments(
  provider: 'doshi' | 'kligman' | 'berliner'
): AppointmentType[] {
  const providerMap = {
    doshi: 'canScheduleForDoshi',
    kligman: 'canScheduleForKligman',
    berliner: 'canScheduleForBerliner',
  };
  const key = providerMap[provider] as keyof AppointmentType;
  return appointmentTypes.filter((apt) => apt[key] === true);
}

export function getAppointmentsByDuration(duration: string): AppointmentType[] {
  return appointmentTypes.filter((apt) => apt.durationCategory === duration);
}

// Statistics for dashboard
export const schedulingStats = {
  total: appointmentTypes.length,
  schedulable: appointmentTypes.filter((apt) => apt.doesTrapezeSchedule).length,
  nonSchedulable: appointmentTypes.filter((apt) => !apt.doesTrapezeSchedule).length,
  providerSpecific: {
    doshi: appointmentTypes.filter((apt) => apt.canScheduleForDoshi).length,
    kligman: appointmentTypes.filter((apt) => apt.canScheduleForKligman).length,
    berliner: appointmentTypes.filter((apt) => apt.canScheduleForBerliner).length,
  },
  byDuration: {
    short: appointmentTypes.filter((apt) => apt.durationCategory === 'short').length,
    medium: appointmentTypes.filter((apt) => apt.durationCategory === 'medium').length,
    long: appointmentTypes.filter((apt) => apt.durationCategory === 'long').length,
    other: appointmentTypes.filter((apt) => apt.durationCategory === 'other').length,
    unknown: appointmentTypes.filter((apt) => apt.durationCategory === 'unknown').length,
  },
};
